import { observable, computed, action } from "mobx";
import * as helpers from "util/helpers";
import { api, apiClient, debug } from "aaa";
import * as _ from "lodash";

export class Loan {

    @observable
    isLoading = true;

    @observable
    method = "sliders";


    @observable
    _amount: number = 0; // floored mean (3) will be computed when the interest-rates have been fetched.
    @computed
    get amount() { return this._amount; }
    set amount(amount: number) {
        this._amount = amount;
        this.recalculate();
    }

    @observable
    _durationInYears: number = 0; // floored mean will be computed when the interest-rates have been fetched.
    @computed
    get durationInYears() { return this._durationInYears; }
    set durationInYears(durationInYears: number) {
        this._durationInYears = helpers.clamp(Math.floor(durationInYears), this.minDuration, this.maxDuration);
        this.recalculate();
    }

    @observable
    monthlyRate: number = 0;

    @observable
    totalAmount: number = 0;

    @observable
    interestSet: api.IInterestSet | undefined;

    @computed
    get interestRate() {
        if (this.interestSet && this.durationInYears >= 1) {
            return this.interestSet.interestInPercent[this.getIndexOfDuration(this.durationInYears)];
        }

        return 0;
    }

    @computed
    get minDuration() {
        if (this.interestSet && this.interestSet.minDuration) {
            return this.interestSet.minDuration;
        }
        return 0;
    }

    @computed
    get maxDuration() {
        if (this.interestSet && this.interestSet.maxDuration) {
            return this.interestSet.maxDuration;
        }
        return 0;
    }

    @computed
    get minAmount() {
        if (this.interestSet && this.interestSet.minAmount) {
            return this.interestSet.minAmount;
        }
        return 0;
    }

    @computed
    get maxAmount() {
        if (this.interestSet && this.interestSet.maxAmount) {
            return this.interestSet.maxAmount;
        }
        return 0;
    }

    getInterestRateForDuration(duration: number) {
        if (this.interestSet) {
            return this.interestSet.interestInPercent[this.getIndexOfDuration(duration)];
        }

        return 0;
    }

    getEffectiveInterestRateForDuration(duration: number) {
        if (this.interestSet) {
            return this.interestSet.interestEffectiveInPercent[this.getIndexOfDuration(duration)];
        }

        return 0;
    }

    // SW-38 (https://allaboutapps-at.atlassian.net/browse/SW-38)
    // the value within the second representative example should be used for the logo
    @computed
    get logoEffectiveInterestRate() {
        if (!this.interestSet || this.interestSet.repExes.length !== 2) {
            return 0;
        }

        return this.interestSet.repExes[1].interestEffective;
    }

    calculate(amount: number, duration: number): { monthlyRate: number; totalAmount: number } {
        if (!this.interestSet
            || this.getIndexOfDuration(duration) > this.interestSet.interestInPercent.length) {
            return {
                monthlyRate: 0,
                totalAmount: 0
            };
        }

        // // PRE https://allaboutapps-at.atlassian.net/browse/SW-30 - Formula taken from Annuitätenrechner.xlsx
        // const interest = this.interestSet.interestInPercent[this.getIndexOfDuration(this.durationInYears)] / 100.0;
        // const factor = 1 + interest;
        // const yearly = amount * (Math.pow(factor, duration) * interest) / (Math.pow(factor, duration) - 1);

        // const result = {
        //     monthlyRate: yearly / 12.0,
        //     totalAmount: yearly * duration
        // };

        // console.log("calculate: old formula", {
        //     amount,
        //     duration,
        //     interest,
        //     factor,
        //     yearly,
        //     result,
        // });

        // POST https://allaboutapps-at.atlassian.net/browse/SW-30
        const effInterest = this.interestSet.interestInPercent[this.getIndexOfDuration(this.durationInYears)] / 100.0;

        // https://www.collect.org/cv11/Help/financialcalculations.html
        // PeriodicPaymentAmount = LoanAmount*(PeriodicInterestRate/100)/(1-(1+PeriodicInterestRate/100)^-NumberOfPeriods)
        const monthlyRate = amount * (effInterest / 12) / (1 - Math.pow(1 + (effInterest / 12), -(duration * 12)));
        const totalAmount = monthlyRate * 12 * duration;

        const resultNew = {
            monthlyRate,
            totalAmount
        };

        // console.log("calculate: new formula", {
        //     amount,
        //     duration,
        //     effInterest,
        //     resultNew
        // });

        return resultNew;
    }

    getIndexOfDuration(duration: number): number {
        return _.indexOf(_.range(this.minDuration, this.maxDuration + 1), duration);
    }

    @computed
    get interestRateEffective() {
        if (this.interestSet && this.durationInYears >= 1) {
            return this.interestSet.interestEffectiveInPercent[this.getIndexOfDuration(this.durationInYears)];
        }

        return 0;
    }

    constructor() {
        this.loadInterestRates();
    }

    @action
    async reload() {
        await this.loadInterestRates();
    }

    /**
     * Load interest rates from backend
     */
    private async loadInterestRates() {
        this.isLoading = true;

        try {
            const request = api.getInterestRates();
            this.interestSet = await apiClient.requestType(request);

            // set default value for range.
            this._amount = getDefaultAmount(this.interestSet.minAmount, this.interestSet.maxAmount);
            this._durationInYears = getDefaultDuration(this.interestSet.minDuration, this.interestSet.maxDuration);

            this.recalculate();
            this.isLoading = false;
        } catch (error) {
            console.error("### Error getting interest rates, retrying in 10sec...", error);
            setTimeout(() => this.loadInterestRates(), 10000);
        }
    }

    /**
     * Recalculate loan values based on user input
     */
    @action
    private recalculate() {
        if (this.durationInYears > 0
            && this.interestSet) {

            const result = this.calculate(this.amount, this.durationInYears);
            this.monthlyRate = result.monthlyRate;
            this.totalAmount = result.totalAmount;
        }
    }
}

export function getDefaultAmount(minAmount: number, maxAmount: number): number {
    return _.floor(_.mean([minAmount, maxAmount]), -3);
}

export function getDefaultDuration(minDuration: number, maxDuration: number): number {
    return _.floor(_.mean([minDuration, maxDuration]), 0);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/models/Loan.ts